"use client";

import * as React from "react";
import { Layers } from "lucide-react";

import { IntegrationBrandIcon } from "@/components/site/primitives/integration-brand-icon";
import { OwnerBadge } from "@/components/site/sections/overview-board/owner-badge";
import {
  LANE_STYLES,
  SYNC_PROVIDER_LABELS,
} from "@/components/site/sections/overview-board/styles";
import { useFlipLayout } from "@/components/site/sections/overview-board/use-flip-layout";
import {
  OVERVIEW_LANES,
  allSteps,
  isViewerItem,
  stepsOwnedByViewer,
  type OverviewDocument,
  type OverviewStep,
} from "@/lib/overview-document";
import { cn } from "@/lib/utils";

type RoadmapFilterValue = "all" | "mine";

/**
 * Four horizons as columns. A narrow board scrolls its lanes sideways and
 * snaps; `lanesBleedClassName` lets the lanes run to the frame's edges and
 * `toolbarClassName` keeps the sync and filter row clear of a frame control.
 */
export function RoadmapBoard({
  document,
  headingLevel,
  lanesBleedClassName,
  toolbarClassName,
  onOpen,
}: {
  document: OverviewDocument;
  headingLevel: 2 | 3 | 4;
  lanesBleedClassName?: string;
  toolbarClassName?: string;
  onOpen: (id: string) => void;
}) {
  const [filter, setFilter] = React.useState<RoadmapFilterValue>("all");
  const lanesRef = React.useRef<HTMLDivElement>(null);
  const captureLanes = useFlipLayout(lanesRef, filter);
  const total = allSteps(document).length;
  const mineCount = stepsOwnedByViewer(document).length;
  const Heading = `h${headingLevel}` as const;
  const CardHeading = `h${headingLevel + 1}` as "h3" | "h4" | "h5";
  const { sync } = document;

  return (
    <section className="@container flex flex-col gap-5">
      <div className={cn("flex flex-wrap items-center justify-between gap-x-6 gap-y-3", toolbarClassName)}>
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
          <Heading className="text-[1.5rem] leading-[1.15] font-semibold tracking-[-0.022em] text-foreground sm:text-[1.625rem]">
            Roadmap{" "}
            <span className="font-medium text-muted-foreground/80 tabular-nums">
              {total}
            </span>
          </Heading>
          <span className="flex items-center gap-2 sm:border-l sm:pl-3.5">
            <span className="text-sm leading-5 font-medium whitespace-nowrap text-muted-foreground">
              Sync with
            </span>
            <span className="flex items-center gap-1.5">
              {sync.providers.map((provider) => (
                <span
                  key={provider}
                  role="img"
                  aria-label={SYNC_PROVIDER_LABELS[provider]}
                  title={SYNC_PROVIDER_LABELS[provider]}
                  className="inline-flex size-9 items-center justify-center rounded-full border border-slate-200 bg-white"
                >
                  <IntegrationBrandIcon slug={provider} size="sm" />
                </span>
              ))}
            </span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            Last sync
            <span
              role="img"
              aria-label={SYNC_PROVIDER_LABELS[sync.lastSync.provider]}
              className="inline-flex size-6 flex-none items-center justify-center rounded-full border bg-background [&_img]:size-3.5"
            >
              <IntegrationBrandIcon slug={sync.lastSync.provider} size="sm" />
            </span>
            <span className="font-medium whitespace-nowrap text-foreground/90">
              {sync.lastSync.at}
            </span>
          </span>
          <RoadmapFilter
            value={filter}
            mineCount={mineCount}
            onChange={(value) => {
              captureLanes();
              setFilter(value);
            }}
          />
        </div>
      </div>

      <div
        ref={lanesRef}
        className={cn(
          "relative snap-x snap-mandatory overflow-x-auto overflow-y-hidden pt-1.5 pb-2 [scrollbar-color:var(--border)_transparent] [scrollbar-width:thin] @min-[900px]:snap-none",
          lanesBleedClassName,
        )}
      >
        <div className="flex items-start gap-3.5 @min-[900px]:grid @min-[900px]:grid-cols-4 @min-[900px]:gap-3">
          {OVERVIEW_LANES.map((lane) => {
            const style = LANE_STYLES[lane];
            const steps = document.roadmap[lane].filter(
              (step) => filter === "all" || isViewerItem(document, step),
            );

            return (
              <div
                key={lane}
                className="flex w-[16.25rem] flex-none snap-start flex-col gap-3 @min-[900px]:w-auto @min-[900px]:min-w-0"
              >
                <div
                  className={cn(
                    "flex items-center gap-2 border-b-2 pb-2.5",
                    style.ruleClassName,
                  )}
                >
                  <span
                    className={cn(
                      "rounded-full border px-2.5 py-[3px] text-xs font-semibold",
                      style.badgeClassName,
                    )}
                  >
                    {style.label}
                  </span>
                  <span className="text-[0.8125rem] font-medium text-muted-foreground/80 tabular-nums">
                    {steps.length}
                  </span>
                </div>
                {steps.map((step) => (
                  <RoadmapStepCard
                    key={step.id}
                    step={step}
                    mine={isViewerItem(document, step)}
                    heading={CardHeading}
                    onOpen={onOpen}
                  />
                ))}
                {steps.length === 0 ? (
                  <p
                    data-flip={`empty-${lane}`}
                    className="rounded-[14px] border border-dashed p-3.5 text-[0.8125rem] text-muted-foreground"
                  >
                    None of your steps here.
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * A roadmap step: title, evidence and owner. The viewer's own steps are dark
 * and say "You". The title button stretches over the card, so the whole card
 * opens the step window from one tab stop.
 */
function RoadmapStepCard({
  step,
  mine,
  heading: CardHeading,
  onOpen,
}: {
  step: OverviewStep;
  mine: boolean;
  heading: "h3" | "h4" | "h5";
  onOpen: (id: string) => void;
}) {
  return (
    <article
      data-flip={step.id}
      className={cn(
        "relative flex flex-col gap-3.5 rounded-[14px] border px-3.5 pt-3.5 pb-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-18px_rgba(0,0,0,0.3)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        mine
          ? "border-foreground bg-foreground text-background has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2"
          : "bg-card has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
      )}
    >
      <CardHeading className="text-[0.9375rem] leading-[1.4] font-semibold tracking-[-0.01em]">
        <button
          type="button"
          aria-haspopup="dialog"
          data-overview-item-trigger={step.id}
          onClick={() => onOpen(step.id)}
          className="text-left decoration-1 underline-offset-4 after:absolute after:inset-0 after:rounded-[14px] after:content-[''] hover:underline focus-visible:outline-none"
        >
          {step.title}
        </button>
      </CardHeading>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
        <span
          className={cn(
            "inline-flex h-7 flex-none items-center gap-1.5 rounded-full border px-2.5 text-[0.6875rem] font-medium",
            mine
              ? "border-background/20 text-background/80"
              : "border-border text-foreground/70",
          )}
        >
          <Layers className="size-3.5" aria-hidden="true" />
          Evidence
          <span
            className={cn(
              "rounded-full px-1.5 py-px text-[0.625rem] font-semibold tabular-nums",
              mine ? "bg-background/15 text-background" : "bg-muted text-foreground/90",
            )}
          >
            {step.signalCount}
          </span>
        </span>
        <OwnerBadge name={step.suggestedOwner} mine={mine} inverted={mine} />
      </div>
    </article>
  );
}

/**
 * "All steps / Mine N". The white pill slides to the pressed segment once it
 * has been measured; before that (server render) the pressed segment paints
 * its own background.
 */
function RoadmapFilter({
  value,
  mineCount,
  onChange,
}: {
  value: RoadmapFilterValue;
  mineCount: number;
  onChange: (value: RoadmapFilterValue) => void;
}) {
  const groupRef = React.useRef<HTMLDivElement>(null);
  const [pill, setPill] = React.useState<{ left: number; width: number } | null>(
    null,
  );

  React.useLayoutEffect(() => {
    const group = groupRef.current;

    if (!group) {
      return;
    }

    const measure = () => {
      const pressed = group.querySelector<HTMLElement>('[aria-pressed="true"]');

      if (pressed) {
        setPill({ left: pressed.offsetLeft, width: pressed.offsetWidth });
      }
    };

    measure();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(group);

    return () => observer?.disconnect();
  }, [value, mineCount]);

  if (mineCount === 0) {
    return null;
  }

  const segment = (selected: boolean) =>
    cn(
      "relative inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[0.8125rem] font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
      selected ? "text-foreground" : "text-foreground/70 hover:text-foreground",
      selected && pill === null && "bg-background shadow-xs",
    );

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label="Which steps to show"
      className="relative inline-flex gap-0.5 rounded-[10px] bg-muted p-[3px]"
    >
      {pill ? (
        <span
          aria-hidden="true"
          className="absolute top-[3px] bottom-[3px] left-0 rounded-lg bg-background shadow-xs transition-[transform,width] duration-[380ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
          style={{ width: pill.width, transform: `translateX(${pill.left}px)` }}
        />
      ) : null}
      <button
        type="button"
        aria-pressed={value === "all"}
        onClick={() => onChange("all")}
        className={segment(value === "all")}
      >
        All steps
      </button>
      <button
        type="button"
        aria-pressed={value === "mine"}
        onClick={() => onChange("mine")}
        className={segment(value === "mine")}
      >
        Mine
        <span className="text-muted-foreground tabular-nums">{mineCount}</span>
      </button>
    </div>
  );
}
